import { LGraphNode, LiteGraph } from "litegraph.js";

export class PatternNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Pattern (Normalized)";

    this.properties = {
      canvasWidth: 1500,
      canvasHeight: 1500,
      gridCount: 100,
      patternName: "random"
    };

    // Cache
    this._cachedPattern = null;
    this._lastSig = null;

    // Slider for gridCount
    this.addWidget("slider", "Cells per Side", 80, (val) => {
      let newVal = Math.round(val);
      this.properties.gridCount = Math.max(1, newVal);
    }, { min: 10, max: 200, step: 1 });

    // Combo for patternName
    this.addWidget("combo", "Pattern", this.properties.patternName, (val) => {
      this.properties.patternName = val;
    }, {
      values: [
        "verticalWave", "horizontalWave", "diagonalWave",
        "spiral", "ripples", "columns", "rows",
        "checkerboard", "concentricSquares", "zigzag", "random"
      ]
    });

    this.addOutput("Pattern", "array");
    this.size = [200, 120];
  }

  onExecute() {
    const propsSig = JSON.stringify({
      w: this.properties.canvasWidth,
      h: this.properties.canvasHeight,
      c: this.properties.gridCount,
      p: this.properties.patternName
    });

    if (this._lastSig !== propsSig) {
      this._lastSig = propsSig;
      this._cachedPattern = this.buildNormalizedPattern();
    }

    this.setOutputData(0, this._cachedPattern);
  }

  buildNormalizedPattern() {
    
    const { gridCount, patternName } = this.properties;

    // Pattern functions returning some raw 'factor'
    const patterns = {
      verticalWave: (i, j) => j * 0.1,
      horizontalWave: (i, j) => i * 0.1,
      diagonalWave: (i, j) => (i + j) * 0.1,
      spiral: (i, j) => Math.sqrt(i * i + j * j) * 0.1,
      ripples: (i, j) =>
        Math.sin(
          Math.sqrt(
            (i - gridCount / 2) ** 2 + (j - gridCount / 2) ** 2
          ) * 2
        ) * 3,
      columns: (i, j) => (i % 2 === 0 ? 1 : 2),
      rows: (i, j) => (j % 2 === 0 ? 1 : 2),
      checkerboard: (i, j) => 0.5 + ((i + j) % 2 === 0 ? 0 : 0.5),
      concentricSquares: (i, j) =>
        Math.min(i, j, gridCount - i - 1, gridCount - j - 1) * 0.1,
      zigzag: (i, j) =>
        ((i + (j % 2 === 0 ? 0 : gridCount - 1 - 2 * i)) % gridCount) * 0.2,
      random: (i, j) => Math.random() * 2
    };

    const currentPattern = patterns[patternName] || patterns.random;

    // raw array
    const rawCells = [];
    let minVal = Infinity;
    let maxVal = -Infinity;

    // compute raw pattern values
    for (let i = 0; i < gridCount; i++) {
      for (let j = 0; j < gridCount; j++) {
        const v = currentPattern(i, j);
        if (v < minVal) minVal = v;
        if (v > maxVal) maxVal = v;

        rawCells.push({ i, j, raw: v });
      }
    }

    // Avoid dividing by zero if pattern is flat
   
    if (maxVal - minVal < 0.00001) {
    
      return rawCells.map((c) => ({
        i: c.i,
        j: c.j,
        normDelay: 0,
        totalCount: gridCount
      }));
    }

    const range = maxVal - minVal;
    const aniStream = rawCells.map((c) => {
      const norm = (c.raw - minVal) / range; // scale to 0..1
      return {
        i: c.i,
        j: c.j,
        normDelay: norm,
        totalCount: gridCount
      };
    });

    // 3) sort by ascending normDelay
    aniStream.sort((a, b) => a.normDelay - b.normDelay);

    return aniStream;
  }
}